// DynamicFields.jsx — small, marketing-scoped config-driven field renderer.
//
// Mirrors the { key, title, type, width } vocabulary the rest of the admin
// uses (providers/washers/up-selling all render fields this way via
// components/common/helper.js), but scoped down to the field types
// marketing actually needs. Zero coupling to Redux `activeItem` — the state
// lives in the caller and is passed in via `value` + `onChange`.
//
// Supported types:
//   text | textarea | number | select | switch | date | subheader | label
//
// Field config extras:
//   dotPath: true          — treats `key` as a dot-path (e.g. 'frequencyCap.perUserDays')
//   options: Array         — for type: 'select' — items shaped as { value, label }
//   width: '3' | '6' | '12' — bootstrap column width (defaults '12')
//   required: true         — visual asterisk only; caller enforces validation
//   placeholder, hint, min, max, rows, disabled, hide
//
// Sections shape (optional): pass elements as an array of { title?, data: [...] }
// to render grouped sections. Or pass a flat array of fields for a plain block.
//
// Sibling components (TriggerForm, SegmentPicker, ChannelPushForm) stay as
// specialized cards — this renderer is intentionally for the boring flat
// stretches of the editor.

import React from 'react';
import Select from 'react-select';
import Switch from 'rc-switch';
import { FormGroup, Input, Label, Row } from 'reactstrap';
import { Colxx } from '../../../../components/common/CustomBootstrap';

// Small get/set helpers for dot-path access into nested state
const readPath = (obj, path) => {
  if (!obj || !path) return undefined;
  const parts = String(path).split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
};

const writePath = (obj, path, value) => {
  const parts = String(path).split('.');
  const clone = { ...(obj || {}) };
  let cur = clone;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    cur[p] = { ...(cur[p] || {}) };
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
  return clone;
};

const selectPortalProps = {
  classNamePrefix: 'rs',
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  styles: { menuPortal: (b) => ({ ...b, zIndex: 9999 }) },
};

// Render a single field. `value` is the whole state object; the field reads
// from `key` (or dot-path if field.dotPath === true) and calls `onChange`
// with a new object where that path is updated.
const Field = ({ field, value, onChange }) => {
  if (field.hide) return null;

  const readValue = () => (field.dotPath ? readPath(value, field.key) : (value ? value[field.key] : undefined));
  const setValue = (v) =>
    onChange(field.dotPath ? writePath(value, field.key, v) : { ...(value || {}), [field.key]: v });

  const currentValue = readValue();

  // --- section-level types ---
  if (field.type === 'subheader') {
    return (
      <Colxx xxs="12" className="mt-2 mb-2">
        <h6 className="mb-0">{field.title}</h6>
        {field.hint && <small className="text-muted d-block">{field.hint}</small>}
      </Colxx>
    );
  }
  if (field.type === 'label') {
    return (
      <Colxx xxs="12" md={field.width || '12'} className="mb-2">
        <div className="text-semi-muted small">{field.title}</div>
        <div style={{ fontSize: '0.9rem' }}>{currentValue ?? '—'}</div>
      </Colxx>
    );
  }

  // --- interactive types ---
  const label = (
    <Label>
      {field.title}
      {field.required && <span className="text-danger">{' *'}</span>}
    </Label>
  );

  let control;
  switch (field.type) {
    case 'text':
      control = (
        <Input
          type="text"
          value={currentValue ?? ''}
          disabled={field.disabled}
          placeholder={field.placeholder || ''}
          maxLength={field.maxLength}
          onChange={(e) => setValue(e.target.value)}
        />
      );
      break;

    case 'textarea':
      control = (
        <Input
          type="textarea"
          rows={field.rows || 3}
          value={currentValue ?? ''}
          disabled={field.disabled}
          placeholder={field.placeholder || ''}
          maxLength={field.maxLength}
          onChange={(e) => setValue(e.target.value)}
        />
      );
      break;

    case 'number':
      control = (
        <Input
          type="number"
          value={currentValue ?? ''}
          disabled={field.disabled}
          placeholder={field.placeholder || ''}
          min={field.min}
          max={field.max}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') return setValue(undefined);
            const n = Number(raw);
            setValue(Number.isFinite(n) ? n : raw);
          }}
        />
      );
      break;

    case 'switch':
      // Inline layout: switch + description-as-label
      return (
        <Colxx xxs="12" md={field.width || '12'} className="mb-3">
          <div className="d-flex align-items-center" style={{ gap: 12 }}>
            <Switch
              className="custom-switch custom-switch-primary"
              checked={!!currentValue}
              disabled={field.disabled}
              onChange={(checked) => setValue(checked)}
            />
            <span>
              <strong>{field.title}</strong>
              {field.hint && (
                <span className="text-muted small d-block">{field.hint}</span>
              )}
            </span>
          </div>
        </Colxx>
      );

    case 'select': {
      const options = field.options || [];
      const selected = options.find((o) => o.value === currentValue) || null;
      control = (
        <Select
          value={selected}
          onChange={(opt) => setValue(opt ? opt.value : null)}
          options={options}
          isDisabled={field.disabled}
          isClearable={!!field.clearable}
          placeholder={field.placeholder || 'Select…'}
          {...selectPortalProps}
        />
      );
      break;
    }

    case 'date':
      control = (
        <Input
          type="date"
          value={currentValue ? new Date(currentValue).toISOString().slice(0, 10) : ''}
          disabled={field.disabled}
          onChange={(e) => setValue(e.target.value ? new Date(e.target.value).getTime() : null)}
        />
      );
      break;

    default:
      control = (
        <div className="text-muted small">
          (Unsupported field type: <code>{field.type}</code>)
        </div>
      );
  }

  return (
    <Colxx xxs="12" md={field.width || '12'}>
      <FormGroup>
        {label}
        {control}
        {field.hint && (
          <small className="text-muted d-block mt-1">{field.hint}</small>
        )}
      </FormGroup>
    </Colxx>
  );
};

// Renders either a flat array of field configs OR an array of section objects
// `{ title, hint, data: [fields] }`. Sections without a title collapse to a
// plain group.
const DynamicFields = ({ value, onChange, elements }) => {
  if (!Array.isArray(elements) || elements.length === 0) return null;

  const isSectioned = elements.some((el) => Array.isArray(el?.data));

  if (!isSectioned) {
    return (
      <Row>
        {elements.map((field, i) => (
          <Field key={field.key || i} field={field} value={value} onChange={onChange} />
        ))}
      </Row>
    );
  }

  return (
    <>
      {elements.map((section, sIndex) => (
        <div key={section.key || sIndex} className={sIndex > 0 ? 'mt-3' : ''}>
          {section.title && (
            <div className="mb-2">
              <h6 className="mb-0">{section.title}</h6>
              {section.hint && <small className="text-muted d-block">{section.hint}</small>}
            </div>
          )}
          <Row>
            {(section.data || []).map((field, i) => (
              <Field key={field.key || i} field={field} value={value} onChange={onChange} />
            ))}
          </Row>
        </div>
      ))}
    </>
  );
};

export default DynamicFields;
